import React, { useState } from 'react';
import { Send, CheckCircle2, X, Wrench, RotateCcw, Plus, Images, Camera } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { SymptomHelper } from './SymptomHelper';
import { getOffer } from '../lyphline';
import { useSettings } from '../contexts/SettingsContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SKETCH = 'https://customer-assets-wrfwihn1.emergentagent.net/job_9e1fb140-210f-412f-9430-9687aa3be1b0/artifacts/tvqsdzi5_image.png';

// x/y are % positions of the hotspot centre over the square sketch image.
const PARTS = [
  { id: 'control', label: 'Control Panel', x: 35, y: 12, issues: ["Won't power on", 'Blank / frozen display', 'Error code showing', 'Buttons unresponsive'] },
  { id: 'gasket', label: 'Door Gasket (rubber seal)', x: 27, y: 29, issues: ['Cracked / torn gasket', 'Not sealing', 'Gasket loose or falling out', 'Frost forming at the seal'] },
  { id: 'door', label: 'Door & Handle', x: 58, y: 42, issues: ["Won't seal / losing vacuum", 'Handle or latch broken', 'Frost or fog on glass'] },
  { id: 'chamber', label: 'Chamber & Trays', x: 37, y: 45, issues: ['Not freezing', 'Ice buildup', 'Product still wet', 'Uneven / incomplete drying'] },
  { id: 'condenser', label: 'Condenser Coils', x: 16, y: 74, issues: ['Frost / ice on coils', 'Coils bent or leaking', 'Dirty / blocked coils', 'Poor vacuum / not condensing'] },
  { id: 'cooling', label: 'Cooling / Compressor', x: 45, y: 77, issues: ['Not getting cold', 'Compressor not running', 'Refrigerant leak suspected', 'Long or failed cycles', 'Overheating'] },
  { id: 'drain', label: 'Drain Valve', x: 53, y: 88, issues: ['Leaking water', 'Valve stuck', "Won't drain"] },
  { id: 'casters', label: 'Casters / Frame', x: 16, y: 93, issues: ['Wheel broken', "Won't roll", 'Rust / frame damage'] },
  { id: 'hose', label: 'Vacuum Hose', x: 65, y: 60, issues: ['Cracked hose', 'Loose fitting / air leak', 'Kinked hose'] },
  { id: 'pump', label: 'Vacuum Pump', x: 88, y: 78, issues: ['Not pulling vacuum', 'Loud / knocking noise', 'Oil leaking', 'Overheating', 'Needs oil change'] },
];

export const InteractiveDiagnostic = ({ segment = '', downtime = null }) => {
  const { settings } = useSettings();
  const CURRENT_OFFER = getOffer(settings);
  const [selected, setSelected] = useState({}); // { partId: { issues:[], note:'' } }
  const [activeId, setActiveId] = useState(null);
  const [helpFor, setHelpFor] = useState(null);
  const [contact, setContact] = useState({ name: '', email: '', phone: '', machine: '', purchase_date: '', notes: '', city: '', province: '', country: '', postal: '', promo_code: '' });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);

  const uploadPhotos = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setUploading(true);
    for (const file of files) {
      const fd = new FormData();
      fd.append('file', file);
      try {
        const res = await axios.post(`${API_URL}/api/leads/diagnostic-photo`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        setPhotos((prev) => [...prev, { url: res.data.file_url, name: res.data.filename }]);
      } catch (err) {
        toast.error(err?.response?.data?.detail || 'Photo upload failed');
      }
    }
    setUploading(false);
  };

  const activePart = PARTS.find((p) => p.id === activeId);
  const flagged = PARTS.filter((p) => {
    const s = selected[p.id];
    return s && (s.issues.length || (s.note && s.note.trim()));
  });

  const toggleIssue = (partId, issue) => {
    setSelected((prev) => {
      const cur = prev[partId] || { issues: [], note: '' };
      const has = cur.issues.includes(issue);
      return { ...prev, [partId]: { ...cur, issues: has ? cur.issues.filter((i) => i !== issue) : [...cur.issues, issue] } };
    });
  };
  const setNote = (partId, note) => setSelected((prev) => ({ ...prev, [partId]: { ...(prev[partId] || { issues: [] }), note } }));
  const clearPart = (partId) => setSelected((prev) => { const n = { ...prev }; delete n[partId]; return n; });
  const reset = () => { setSelected({}); setActiveId(null); };

  const submit = async (e) => {
    e.preventDefault();
    if (flagged.length === 0) { toast.error('Tap a part of the machine to flag what\'s wrong first.'); return; }
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/leads/diagnostic`, {
        name: contact.name,
        email: contact.email,
        phone: contact.phone,
        machine: contact.machine,
        purchase_date: contact.purchase_date,
        city: contact.city,
        province: contact.province,
        country: contact.country,
        postal_code: contact.postal,
        promo_code: contact.promo_code,
        message: [contact.notes, photos.length ? `Photos: ${photos.map(p => `${API_URL}${p.url}`).join(' | ')}` : '', downtime && downtime.total ? `Downtime calculator: ~$${Math.round(downtime.total).toLocaleString()} at risk (${downtime.cycleHours}h cycle × $${downtime.valuePerBatch}/batch × ${downtime.daysDown} days)` : ''].filter(Boolean).join(', '),
        segment,
        parts: flagged.map((p) => ({ part: p.label, issues: selected[p.id].issues, note: selected[p.id].note || '' })),
        photos: photos.map(p => `${API_URL}${p.url}`),
      });
      setDone(true);
      toast.success('Diagnostic sent! Our rebuild team will follow up.');
    } catch (err) {
      console.error('Diagnostic submit failed', err?.response?.data || err);
      toast.error('Something went wrong. Please try again or call us.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="diagnose" className="py-20 section-padding bg-[#002B47]" data-testid="interactive-diagnostic">
      <div className="max-w-7xl mx-auto">
        <div className="max-w-2xl mb-10">
          <span className="text-[#CE8946] font-semibold text-xs uppercase tracking-[0.2em]">Not sure what's wrong?</span>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mt-3">Point to where you think the problem is</h2>
          <p className="text-white/60 mt-3">You don't need to know the exact part. Tap wherever you think the trouble might be, based on what you're hearing, seeing or smelling, and we'll take it from there.</p>
        </div>

        {done ? (
          <div className="bg-[#023F63] border border-white/10 rounded-2xl p-12 text-center max-w-xl mx-auto" data-testid="diagnostic-success">
            <CheckCircle2 className="h-14 w-14 text-[#CE8946] mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-white">Diagnostic sent!</h3>
            <p className="text-white/60 mt-2">Thanks, our rebuild team has your flagged parts and will reach out shortly with next steps.</p>
            <button onClick={() => { setDone(false); reset(); setContact({ name: '', email: '', phone: '', machine: '', purchase_date: '', notes: '', city: '', province: '', country: '', postal: '', promo_code: '' }); }} className="inline-flex items-center gap-2 mt-6 text-[#CE8946] font-semibold" data-testid="diagnostic-restart">
              <RotateCcw className="h-4 w-4" /> Start another
            </button>
          </div>
        ) : (
          <div className="grid lg:grid-cols-2 gap-8 items-start">
            {/* Diagram */}
            <div className="bg-white rounded-2xl p-4 relative">
              <div className="relative w-full max-w-[520px] mx-auto aspect-square">
                <img src={SKETCH} alt="Freeze dryer diagram, tap a part to report a problem" className="w-full h-full object-contain select-none pointer-events-none" />
                {PARTS.map((p) => {
                  const isFlagged = flagged.some((fp) => fp.id === p.id);
                  const isActive = activeId === p.id;
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => setActiveId(p.id)}
                      style={{ left: `${p.x}%`, top: `${p.y}%` }}
                      className={`absolute -translate-x-1/2 -translate-y-1/2 group flex items-center gap-1.5 rounded-full transition-all ${isActive ? 'z-20' : 'z-10'}`}
                      data-testid={`hotspot-${p.id}`}
                      aria-label={p.label}
                    >
                      <span className={`relative flex h-6 w-6 items-center justify-center rounded-full border-2 font-bold text-xs
                        ${isFlagged ? 'bg-[#CE8946] border-[#CE8946] text-[#003456]' : 'bg-white border-[#B87333] text-[#B87333]'}
                        ${isActive ? 'ring-4 ring-[#CE8946]/30' : ''}`}>
                        {isFlagged ? <CheckCircle2 className="h-4 w-4" /> : '+'}
                        {!isFlagged && <span className="absolute inline-flex h-full w-full rounded-full bg-[#CE8946]/40 animate-ping" />}
                      </span>
                      <span className={`hidden sm:inline text-[11px] font-semibold px-2 py-0.5 rounded-md whitespace-nowrap
                        ${isFlagged ? 'bg-[#CE8946] text-[#003456]' : 'bg-[#003456] text-white'} ${isActive ? '' : 'opacity-0 group-hover:opacity-100'} transition-opacity`}>
                        {p.label}
                      </span>
                    </button>
                  );
                })}
              </div>
              <p className="text-center text-xs text-slate-400 mt-2">Tap where you think the problem is, a guess is fine{flagged.length > 0 ? ` · ${flagged.length} flagged` : ''}</p>
            </div>

            {/* Panel */}
            <div className="space-y-5">
              {/* Active part issue picker */}
              {activePart ? (
                <div className="bg-[#023F63] border border-white/10 rounded-2xl p-6" data-testid="issue-picker">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-lg font-bold text-white">{activePart.label}</h3>
                    <button onClick={() => setActiveId(null)} className="text-white/50 hover:text-white" aria-label="Close"><X className="h-5 w-5" /></button>
                  </div>
                  <p className="text-sm text-white/50 mb-4">What are you noticing here? A best guess is fine, pick whatever sounds or looks right.</p>
                  <button type="button" onClick={() => setHelpFor(activePart.id)} className="inline-flex items-center gap-2 mb-4 text-sm font-semibold text-[#CE8946] hover:text-[#B87333]" data-testid="open-symptom-helper">
                    <Images className="h-4 w-4" /> Not sure? See photos &amp; common symptoms
                  </button>
                  <div className="flex flex-wrap gap-2">
                    {activePart.issues.map((issue) => {
                      const on = (selected[activePart.id]?.issues || []).includes(issue);
                      return (
                        <button key={issue} onClick={() => toggleIssue(activePart.id, issue)}
                          className={`text-sm px-3 py-1.5 rounded-full border transition-colors ${on ? 'bg-[#CE8946] border-[#CE8946] text-[#003456] font-semibold' : 'border-white/20 text-white/80 hover:border-[#CE8946]'}`}
                          data-testid={`issue-${activePart.id}-${issue.slice(0, 6).replace(/\W/g, '').toLowerCase()}`}>
                          {issue}
                        </button>
                      );
                    })}
                  </div>
                  <textarea
                    value={selected[activePart.id]?.note || ''}
                    onChange={(e) => setNote(activePart.id, e.target.value)}
                    rows={2}
                    placeholder="Anything else about this part? (optional)"
                    className="w-full mt-4 px-4 py-2.5 rounded-md bg-white/10 border border-white/20 text-white placeholder-white/40 text-sm focus:outline-none focus:ring-2 focus:ring-[#CE8946] resize-none"
                    data-testid={`note-${activePart.id}`}
                  />
                  <button type="button" onClick={() => setActiveId(null)}
                    className="w-full mt-4 inline-flex items-center justify-center gap-2 border border-[#CE8946]/40 text-[#CE8946] hover:bg-[#CE8946]/10 font-semibold px-4 py-2.5 rounded-md text-sm transition-colors"
                    data-testid={`add-to-diagnostic-${activePart.id}`}>
                    <Plus className="h-4 w-4" /> Add to my diagnostic
                  </button>
                </div>
              ) : (
                <div className="bg-[#023F63] border border-dashed border-white/15 rounded-2xl p-6 text-white/50 text-sm flex items-center gap-3">
                  <Wrench className="h-5 w-5 text-[#CE8946]" /> Tap wherever you think the trouble is to get started, a guess is fine.
                </div>
              )}

              {/* Running summary */}
              {flagged.length > 0 && (
                <div className="bg-[#023F63] border border-white/10 rounded-2xl p-6" data-testid="diagnostic-summary">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-bold uppercase tracking-widest text-white/50">Your diagnostic</h4>
                    <button onClick={reset} className="text-xs text-white/50 hover:text-white flex items-center gap-1"><RotateCcw className="h-3 w-3" /> Clear</button>
                  </div>
                  <ul className="space-y-2">
                    {flagged.map((p) => (
                      <li key={p.id} className="flex items-start justify-between gap-3 text-sm">
                        <span className="text-white/80"><span className="font-semibold text-white">{p.label}:</span> {selected[p.id].issues.join(', ') || 'flagged'}{selected[p.id].note ? `, ${selected[p.id].note}` : ''}</span>
                        <button onClick={() => clearPart(p.id)} className="text-white/40 hover:text-white flex-shrink-0"><X className="h-4 w-4" /></button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Contact + submit */}
              <form onSubmit={submit} className="bg-[#023F63] border border-white/10 rounded-2xl p-6 space-y-3" data-testid="diagnostic-contact">
                <h4 className="text-sm font-bold uppercase tracking-widest text-white/50">Where do we send your diagnostic?</h4>
                <div className="grid sm:grid-cols-2 gap-3">
                  <input required value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} placeholder="Full name *" className="px-4 py-2.5 rounded-md bg-white/10 border border-white/20 text-white placeholder-white/40 text-sm focus:outline-none focus:ring-2 focus:ring-[#CE8946]" data-testid="diagnostic-name" />
                  <input required type="email" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} placeholder="Email *" className="px-4 py-2.5 rounded-md bg-white/10 border border-white/20 text-white placeholder-white/40 text-sm focus:outline-none focus:ring-2 focus:ring-[#CE8946]" data-testid="diagnostic-email" />
                  <input value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} placeholder="Phone" className="px-4 py-2.5 rounded-md bg-white/10 border border-white/20 text-white placeholder-white/40 text-sm focus:outline-none focus:ring-2 focus:ring-[#CE8946]" data-testid="diagnostic-phone" />
                  <input value={contact.machine} onChange={(e) => setContact({ ...contact, machine: e.target.value })} placeholder="Make / model (if known)" className="px-4 py-2.5 rounded-md bg-white/10 border border-white/20 text-white placeholder-white/40 text-sm focus:outline-none focus:ring-2 focus:ring-[#CE8946]" data-testid="diagnostic-machine" />
                  <div className="sm:col-span-2">
                    <label className="block text-xs text-white/50 mb-1.5">Approx. date of purchase</label>
                    <input type="month" value={contact.purchase_date} onChange={(e) => setContact({ ...contact, purchase_date: e.target.value })} className="w-full px-4 py-2.5 rounded-md bg-white/10 border border-white/20 text-white placeholder-white/40 text-sm focus:outline-none focus:ring-2 focus:ring-[#CE8946]" data-testid="diagnostic-purchase-date" />
                  </div>
                  <div className="sm:col-span-2 pt-1">
                    <label className="block text-xs text-white/50 mb-1.5">Where is the machine? <span className="text-white/40">(for a shipping quote)</span></label>
                  </div>
                  <input required value={contact.city} onChange={(e) => setContact({ ...contact, city: e.target.value })} placeholder="City / Town *" className="px-4 py-2.5 rounded-md bg-white/10 border border-white/20 text-white placeholder-white/40 text-sm focus:outline-none focus:ring-2 focus:ring-[#CE8946]" data-testid="diagnostic-city" />
                  <input value={contact.province} onChange={(e) => setContact({ ...contact, province: e.target.value })} placeholder="Province / State" className="px-4 py-2.5 rounded-md bg-white/10 border border-white/20 text-white placeholder-white/40 text-sm focus:outline-none focus:ring-2 focus:ring-[#CE8946]" data-testid="diagnostic-province" />
                  <input value={contact.country} onChange={(e) => setContact({ ...contact, country: e.target.value })} placeholder="Country" className="px-4 py-2.5 rounded-md bg-white/10 border border-white/20 text-white placeholder-white/40 text-sm focus:outline-none focus:ring-2 focus:ring-[#CE8946]" data-testid="diagnostic-country" />
                  <input value={contact.postal} onChange={(e) => setContact({ ...contact, postal: e.target.value })} placeholder="Postal / ZIP code" className="px-4 py-2.5 rounded-md bg-white/10 border border-white/20 text-white placeholder-white/40 text-sm focus:outline-none focus:ring-2 focus:ring-[#CE8946]" data-testid="diagnostic-postal" />
                </div>
                <div className="pt-1">
                  <label className="block text-xs text-white/50 mb-2">Add photos of the machine or problem <span className="text-white/40">(optional, helps us diagnose)</span></label>
                  <label className="flex items-center justify-center gap-2 w-full px-4 py-3 rounded-md border border-dashed border-white/30 text-white/70 text-sm cursor-pointer hover:border-[#CE8946] hover:text-white transition-colors" data-testid="diagnostic-photo-label">
                    <Camera className="h-4 w-4 text-[#CE8946]" />
                    {uploading ? 'Uploading…' : 'Tap to add photos'}
                    <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => uploadPhotos(e.target.files)} data-testid="diagnostic-photo-input" />
                  </label>
                  {photos.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3" data-testid="diagnostic-photo-previews">
                      {photos.map((p, i) => (
                        <div key={i} className="relative">
                          <img src={`${API_URL}${p.url}`} alt={p.name} className="h-16 w-16 object-cover rounded-md border border-white/20" />
                          <button type="button" onClick={() => setPhotos(photos.filter((_, idx) => idx !== i))} className="absolute -top-2 -right-2 bg-[#003456] border border-white/30 text-white rounded-full h-5 w-5 flex items-center justify-center text-xs" data-testid={`diagnostic-photo-remove-${i}`}>×</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                  <div className="rounded-md border border-[#CE8946]/40 bg-[#CE8946]/10 p-3">
                    <p className="text-sm text-white"><span className="font-bold text-[#CE8946]">Current offer:</span> {CURRENT_OFFER.headline}</p>
                    <p className="text-xs text-white/60 mt-0.5">{CURRENT_OFFER.detail}</p>
                    <input value={contact.promo_code} onChange={(e) => setContact({ ...contact, promo_code: e.target.value })} placeholder={`Offer code (e.g. ${CURRENT_OFFER.code})`} className="w-full mt-2 px-4 py-2.5 rounded-md bg-white/10 border border-white/20 text-white placeholder-white/40 text-sm focus:outline-none focus:ring-2 focus:ring-[#CE8946]" data-testid="diagnostic-promo" />
                  </div>
                )}
                <div>
                  <label className="block text-xs text-white/50 mb-1.5">Describe what you're hearing, seeing or smelling (optional)</label>
                  <textarea value={contact.notes} onChange={(e) => setContact({ ...contact, notes: e.target.value })} rows={3} placeholder="Extra notes, history, error codes, what you've already tried, symptoms…" className="w-full px-4 py-2.5 rounded-md bg-white/10 border border-white/20 text-white placeholder-white/40 text-sm focus:outline-none focus:ring-2 focus:ring-[#CE8946] resize-none" data-testid="diagnostic-notes" />
                </div>
                {downtime && downtime.total > 0 && (
                  <p className="text-xs text-white/70 bg-white/5 border border-[#CE8946]/30 rounded-md px-3 py-2" data-testid="diagnostic-downtime-note">
                    From your downtime calculator: <span className="text-[#CE8946] font-semibold">~${Math.round(downtime.total).toLocaleString()} at risk</span>, we'll flag your request as time-sensitive.
                  </p>
                )}
                <button type="submit" disabled={loading} className="w-full inline-flex items-center justify-center gap-2 bg-[#CE8946] hover:bg-[#B87333] text-[#003456] font-bold px-6 py-3 rounded-md transition-colors disabled:opacity-60" data-testid="diagnostic-submit">
                  {loading ? 'Sending…' : (<>Send My Diagnostic {flagged.length > 0 ? `(${flagged.length})` : ''} <Send className="h-4 w-4" /></>)}
                </button>
              </form>
            </div>
          </div>
        )}
      </div>

      {helpFor && (
        <SymptomHelper
          partId={helpFor}
          partLabel={(PARTS.find((p) => p.id === helpFor) || {}).label}
          selectedIssues={selected[helpFor]?.issues || []}
          onToggle={(issue) => toggleIssue(helpFor, issue)}
          onClose={() => setHelpFor(null)}
        />
      )}
    </section>
  );
};

export default InteractiveDiagnostic;
